import React, { useState, useEffect } from 'react';
import { X, Home, Image, Save } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

export default function PropertyFormModal({ property, communes, selectedCommuneId, onSave, onClose }) {
  const [formData, setFormData] = useState({
    title: '',
    address: '',
    description: '',
    property_type: 'apartment',
    status: 'available',
    price: '',
    surface_area: '',
    bedrooms: '',
    bathrooms: '',
    commune_id: selectedCommuneId || '',
    features: {
      pool: false,
      garden: false,
      parking: false,
      elevator: false,
      balcony: false,
      terrace: false,
      security: false,
      furnished: false
    },
    images: [],
    videos: [],
    coordinates: {
      lat: '',
      lng: ''
    }
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (property) {
      setFormData({
        title: property?.title || '',
        address: property?.address || '',
        description: property?.description || '',
        property_type: property?.property_type || 'apartment',
        status: property?.status || 'available',
        price: property?.price ? property?.price?.toString() : '',
        surface_area: property?.surface_area ? property?.surface_area?.toString() : '',
        bedrooms: property?.bedrooms ? property?.bedrooms?.toString() : '',
        bathrooms: property?.bathrooms ? property?.bathrooms?.toString() : '',
        commune_id: property?.commune_id || selectedCommuneId || '',
        features: property?.features || {
          pool: false,
          garden: false,
          parking: false,
          elevator: false,
          balcony: false,
          terrace: false,
          security: false,
          furnished: false
        },
        images: property?.images || [],
        videos: property?.videos || [],
        coordinates: property?.coordinates || { lat: '', lng: '' }
      });
    } else if (selectedCommuneId) {
      setFormData(prev => ({ ...prev, commune_id: selectedCommuneId }));
    }
  }, [property, selectedCommuneId]);

  const propertyTypes = [
    { value: 'apartment', label: 'Appartement' },
    { value: 'house', label: 'Maison' },
    { value: 'villa', label: 'Villa' },
    { value: 'land', label: 'Terrain' },
    { value: 'commercial', label: 'Commercial' }
  ];

  const statusOptions = [
    { value: 'available', label: 'Disponible' },
    { value: 'reserved', label: 'Réservé' },
    { value: 'sold', label: 'Vendu' },
    { value: 'withdrawn', label: 'Retiré' }
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.title?.trim()) {
      newErrors.title = 'Le titre est requis';
    }
    
    if (!formData?.address?.trim()) {
      newErrors.address = 'L\'adresse est requise';
    }
    
    if (!formData?.commune_id) {
      newErrors.commune_id = 'La commune est requise';
    }
    
    if (!formData?.price || isNaN(parseFloat(formData?.price))) {
      newErrors.price = 'Le prix est requis et doit être un nombre';
    }
    
    if (formData?.surface_area && isNaN(parseFloat(formData?.surface_area))) {
      newErrors.surface_area = 'La surface doit être un nombre';
    }
    
    if (formData?.bedrooms && isNaN(parseInt(formData?.bedrooms))) {
      newErrors.bedrooms = 'Le nombre de chambres doit être un nombre';
    }
    
    if (formData?.bathrooms && isNaN(parseInt(formData?.bathrooms))) {
      newErrors.bathrooms = 'Le nombre de salles de bain doit être un nombre';
    }
    
    if (formData?.coordinates?.lat && isNaN(parseFloat(formData?.coordinates?.lat))) {
      newErrors.lat = 'La latitude doit être un nombre';
    }
    
    if (formData?.coordinates?.lng && isNaN(parseFloat(formData?.coordinates?.lng))) {
      newErrors.lng = 'La longitude doit être un nombre';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const submitData = {
        title: formData?.title?.trim(),
        address: formData?.address?.trim(),
        description: formData?.description?.trim() || null,
        property_type: formData?.property_type,
        status: formData?.status,
        price: parseFloat(formData?.price),
        surface_area: formData?.surface_area ? parseFloat(formData?.surface_area) : null,
        bedrooms: formData?.bedrooms ? parseInt(formData?.bedrooms) : null,
        bathrooms: formData?.bathrooms ? parseInt(formData?.bathrooms) : null,
        commune_id: formData?.commune_id,
        features: formData?.features,
        images: formData?.images,
        videos: formData?.videos,
        coordinates: (formData?.coordinates?.lat && formData?.coordinates?.lng) ? {
          lat: parseFloat(formData?.coordinates?.lat),
          lng: parseFloat(formData?.coordinates?.lng)
        } : null
      };
      
      await onSave(submitData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureChange = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev?.features,
        [feature]: !prev?.features?.[feature]
      }
    }));
  };

  const featuresList = [
    { key: 'pool', label: 'Piscine' },
    { key: 'garden', label: 'Jardin' },
    { key: 'parking', label: 'Parking' },
    { key: 'elevator', label: 'Ascenseur' },
    { key: 'balcony', label: 'Balcon' },
    { key: 'terrace', label: 'Terrasse' },
    { key: 'security', label: 'Sécurité' },
    { key: 'furnished', label: 'Meublé' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold flex items-center">
            <Home className="w-6 h-6 mr-2 text-green-600" />
            {property ? 'Modifier la propriété' : 'Nouvelle propriété'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900 border-b pb-2">Informations de base</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titre *
                </label>
                <Input
                  value={formData?.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e?.target?.value }))}
                  placeholder="Titre de la propriété"
                  className={errors?.title ? 'border-red-300' : ''}
                />
                {errors?.title && (
                  <p className="text-sm text-red-600 mt-1">{errors?.title}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse *
                </label>
                <Input
                  value={formData?.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e?.target?.value }))}
                  placeholder="Adresse complète"
                  className={errors?.address ? 'border-red-300' : ''}
                />
                {errors?.address && (
                  <p className="text-sm text-red-600 mt-1">{errors?.address}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Commune *
                </label>
                <Select
                  value={formData?.commune_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, commune_id: e?.target?.value }))}
                  className={errors?.commune_id ? 'border-red-300' : ''}
                >
                  <option value="">Sélectionner une commune</option>
                  {communes?.map((commune) => (
                    <option key={commune?.id} value={commune?.id}>
                      {commune?.name} - {commune?.district}
                    </option>
                  ))}
                </Select>
                {errors?.commune_id && (
                  <p className="text-sm text-red-600 mt-1">{errors?.commune_id}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type *
                  </label>
                  <Select
                    value={formData?.property_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, property_type: e?.target?.value }))}
                  >
                    {propertyTypes?.map((type) => (
                      <option key={type?.value} value={type?.value}>
                        {type?.label}
                      </option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Statut *
                  </label>
                  <Select
                    value={formData?.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e?.target?.value }))}
                  >
                    {statusOptions?.map((status) => (
                      <option key={status?.value} value={status?.value}>
                        {status?.label}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prix (XOF) *
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData?.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: e?.target?.value }))}
                  placeholder="Prix"
                  className={errors?.price ? 'border-red-300' : ''}
                />
                {errors?.price && (
                  <p className="text-sm text-red-600 mt-1">{errors?.price}</p>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Surface (m²)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData?.surface_area}
                    onChange={(e) => setFormData(prev => ({ ...prev, surface_area: e?.target?.value }))}
                    placeholder="Surface"
                    className={errors?.surface_area ? 'border-red-300' : ''}
                  />
                  {errors?.surface_area && (
                    <p className="text-sm text-red-600 mt-1">{errors?.surface_area}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Chambres
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={formData?.bedrooms}
                    onChange={(e) => setFormData(prev => ({ ...prev, bedrooms: e?.target?.value }))}
                    placeholder="Nombre"
                    className={errors?.bedrooms ? 'border-red-300' : ''}
                  />
                  {errors?.bedrooms && (
                    <p className="text-sm text-red-600 mt-1">{errors?.bedrooms}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Salles de bain
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={formData?.bathrooms}
                    onChange={(e) => setFormData(prev => ({ ...prev, bathrooms: e?.target?.value }))}
                    placeholder="Nombre"
                    className={errors?.bathrooms ? 'border-red-300' : ''}
                  />
                  {errors?.bathrooms && (
                    <p className="text-sm text-red-600 mt-1">{errors?.bathrooms}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData?.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
                  placeholder="Description de la propriété"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>
            </div>

            {/* Features and Location */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900 border-b pb-2">Caractéristiques</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Équipements
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {featuresList?.map((feature) => (
                    <label key={feature?.key} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData?.features?.[feature?.key] || false}
                        onChange={() => handleFeatureChange(feature?.key)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{feature?.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-3">Localisation</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Latitude
                    </label>
                    <Input
                      type="number"
                      step="any"
                      value={formData?.coordinates?.lat}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        coordinates: { ...prev?.coordinates, lat: e?.target?.value }
                      }))}
                      placeholder="Ex: 5.3364"
                      className={errors?.lat ? 'border-red-300' : ''}
                    />
                    {errors?.lat && (
                      <p className="text-sm text-red-600 mt-1">{errors?.lat}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Longitude
                    </label>
                    <Input
                      type="number"
                      step="any"
                      value={formData?.coordinates?.lng}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        coordinates: { ...prev?.coordinates, lng: e?.target?.value }
                      }))}
                      placeholder="Ex: -4.0267"
                      className={errors?.lng ? 'border-red-300' : ''}
                    />
                    {errors?.lng && (
                      <p className="text-sm text-red-600 mt-1">{errors?.lng}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Note about media files */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Image className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900">Médias</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Les images et vidéos peuvent être ajoutées après la création de la propriété 
                      via le système de gestion des médias.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex items-center"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {property ? 'Mettre à jour' : 'Créer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}